import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CurrencyDollarIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageContent } from '@/components/page-content';
import { Card, CardContent } from '@/components/ui/card';
import { SelectItem } from '@/components/ui/select';
import { SectionHeader } from '@/components/section-header';
import { useAppForm } from '@/hooks/use-app-form';
import { ReportingCurrencyService } from '@/api/reporting-currency-service';

export const Route = createFileRoute(
  '/_auth/_side-layout/settings_/organization/currency',
)({
  component: () => (
    <PageContent>
      <ReportingCurrencyCard />
    </PageContent>
  ),
});

// A small, common ISO-4217 set for the reporting-currency picker (mirrors the
// project base-currency list). V1 stores amounts verbatim and does no FX.
const CURRENCY_OPTIONS = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'JPY',
  'CHF',
  'SEK',
  'NOK',
  'DKK',
  'INR',
  'BRL',
] as const;

// Org-level reporting currency for cross-project, org-scoped revenue rollups
// (companies span projects, so there's no single project base currency). When
// unset, rollups fall back to the org's earliest project base currency, else
// USD — surfaced as the placeholder.
function ReportingCurrencyCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['reporting-currency'],
    queryFn: () => ReportingCurrencyService.get(),
  });

  return (
    <Card>
      <CardContent className="space-y-4">
        <SectionHeader
          icon={<CurrencyDollarIcon size={20} />}
          title="Reporting Currency"
          subTitle="The currency for org-wide revenue rollups (e.g. company revenue, which spans projects). Amounts are stored in their original currency — V1 does not convert between currencies, so revenue in other currencies is excluded from these totals."
        />
        {isLoading || !data ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : (
          <ReportingCurrencyForm
            // When unset, seed the picker with the resolved currency so the user
            // sees what the rollups actually use today. key on the seed value so
            // the form re-inits if the query later returns a different currency.
            key={data.reportingCurrency ?? data.resolvedCurrency ?? 'USD'}
            initial={data.reportingCurrency ?? data.resolvedCurrency ?? 'USD'}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ReportingCurrencyForm({ initial }: { initial: string }) {
  const queryClient = useQueryClient();

  const form = useAppForm({
    defaultValues: { reportingCurrency: initial },
    onSubmit: async ({ value }) => {
      try {
        await ReportingCurrencyService.update(value.reportingCurrency);
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : 'Could not update reporting currency',
        );
        return;
      }
      // Company revenue everywhere is scoped to this currency; refresh.
      queryClient.invalidateQueries({ queryKey: ['reporting-currency'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      form.reset({ reportingCurrency: value.reportingCurrency });
      toast.success('Reporting currency updated');
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.AppForm>
        <form.AppField name="reportingCurrency">
          {(field) => (
            <field.Select
              label="Organization Revenue Currency"
              fieldClassName="max-w-xs"
            >
              {CURRENCY_OPTIONS.map((code) => (
                <SelectItem key={code} value={code}>
                  {code}
                </SelectItem>
              ))}
            </field.Select>
          )}
        </form.AppField>
        <form.SubmitButton />
      </form.AppForm>
    </form>
  );
}
